import React from 'react';

import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import Lozenge from '@atlaskit/lozenge';
import Diff, { DiffContent } from '@atlassian/bitkit-diff';
import { DiffEntry } from '@atlassian/bitkit-diff/exports/types';
import File from '@atlassian/bitkit-file';
// @ts-ignore TODO: fix noImplicitAny error here
import diffParser from '@atlassian/diffparser';

import {
  ApiCommentOrPlaceholder,
  InlineField,
} from 'src/components/conversation-provider/types';
import { toConversationId } from 'src/components/conversation-provider/utils/to-conversation';
import { CommentThread } from 'src/components/conversation/src/components/comment-tree';
import { getGlobalTabSize } from 'src/redux/diff-settings';
import { Diff as DiffType } from 'src/types/pull-request';

import { PullRequestConversation } from '../conversation/pull-request-conversation';

import messages from './comments-dialog.i18n';

/** Conversation options passed to functions for rendering conversations
 *  in the outdated and review dialogs. Discriminated union of `ReviewDialogOptions` and `OutdatedDialogOptions`
 *  allow for different behavior when rendering conversations in each dialog.
 */
export type RenderConversationOptions = {
  isColorBlindModeEnabled: boolean;
  isSyntaxHighlightingEnabled: boolean;
  canCollapseThread?: boolean;
  canResolveThread?: boolean;
  canCollapseFile?: boolean;
  isCommentThreadHighlighted?: boolean | null | undefined;
  highlightedCommentId?: number | null;
  handleEditorOpen?: () => void;
  handleEditorClose?: () => void;
  previousFilePath?: string;
  meta?: Pick<InlineField, 'to' | 'path'>;
  showLikeActionOnly?: boolean;
  renderWithWrapper?: (
    conversationId: string,
    options: RenderConversationOptions,
    PRConversation: JSX.Element
  ) => JSX.Element;
  handleFileExpandCollapse?: (isExpanded: boolean | undefined) => void;
  handlePermalinkClick?: () => void;
};

/**
 * Used by the outdated comment dialog to determine if the conversation should be highlighted visually.
 * This is relevant in the case where the outdated conversation is linked in the URL or navigated to
 * by the comment navigation.
 * @param conversation Relevant conversation to be compared to the highlighed commentId
 * @param isCommentThreadHighlighted Is `true` if a comment thread is linked to in the URL or navigated to by the comment navigation
 * @param highlightedCommentId Specific comment Id in the linked conversation that needs to be highlighted
 * @returns a boolean indicating if the conversation should be highlighted
 */
export const shouldHighlightCurrentConversation = (
  conversationId: string,
  isCommentThreadHighlighted: boolean | null | undefined,
  highlightedCommentId: number | null | undefined
): boolean => {
  if (!(isCommentThreadHighlighted && highlightedCommentId)) {
    return false;
  }

  return (
    conversationId.replace('conversation-', '') === String(highlightedCommentId)
  );
};

/**
 * Function to render the `PullRequestConversation` object of a conversation in the review or outdated dialogs. The outdated comment
 * dialog renders the conversation in a `CommentThreadWrapper` to highlight a comment in the conversation while the review dialog just renders the base conversation.
 * @param conversation Conversation to be rendered
 * @param options Conversations options used to determine different behavior for both the review and outdated dialogs
 * @returns JSX element of the rendered conversation
 */
const renderConversation = (
  commentThread: CommentThread,
  options: RenderConversationOptions
): JSX.Element => {
  const conversationId = toConversationId(commentThread.topComment);
  const prConversation = (
    <PullRequestConversation
      key={conversationId}
      portalParentId={`portal-parent-pr-dialog-conversation-${conversationId}`}
      meta={options.meta}
      onEditorOpen={options.handleEditorOpen}
      onEditorClose={options.handleEditorClose}
      showLikeActionOnly={options.showLikeActionOnly}
      canCollapseThread={options.canCollapseThread}
      canResolveThread={options.canResolveThread}
      commentThreads={[commentThread]}
      onCommentPermalinkClick={options.handlePermalinkClick}
    />
  );

  return options.renderWithWrapper
    ? options.renderWithWrapper(conversationId, options, prConversation)
    : prConversation;
};

/**
 * Renders the conversation only if it is inline with the diff
 * @param content Diff content being truthy with valid metadata renders the conversation
 * @param conversation Conversation object to be rendered
 * @param options Conversation options passed to the `renderConversation` function
 * @returns The JSX of the rendered conversation or null if the conversation is not inline
 */
const renderInLineConversation = (
  content: DiffContent,
  commentThread: CommentThread,
  options: RenderConversationOptions
) => {
  if (content) {
    const meta = commentThread.topComment.inline;
    return content.lineTo === meta?.to || content.lineFrom === meta?.from
      ? renderConversation(commentThread, options)
      : null;
  } else {
    return null;
  }
};

/**
 * Renders the conversation inline with its corresponding diff
 * @param diff Diff of the file to be rendered
 * @param conversation Related conversation passed inline to `Diff` component
 * @param options Pass options to render the conversation and define a `previousFilepath` if the conversation is outdated
 * @returns JSX element of the diff and inline conversation rendered within a file header
 */
const renderInDiffConversation = (
  diff: DiffEntry,
  commentThread: CommentThread,
  options: RenderConversationOptions,
  tabSize: number
): JSX.Element => (
  <File
    filePath={commentThread.topComment.inline?.path || ''}
    prevFilePath={options.previousFilePath}
    isCollapsible={options.canCollapseFile}
    // we'll show an outdated lozenge in the file header if the conversations is out-of-date
    renderBeforeActions={() =>
      commentThread.topComment.inline?.outdated && (
        <Lozenge appearance="moved">
          <FormattedMessage {...messages.fileHeaderLozenge} />
        </Lozenge>
      )
    }
    toggleExpanded={options.handleFileExpandCollapse}
  >
    <Diff
      tabSize={tabSize}
      filePath={commentThread.topComment.inline?.path || undefined}
      diff={diff}
      isSideBySide={false}
      inlineContent={content =>
        renderInLineConversation(content, commentThread, options)
      }
      isColorBlindModeEnabled={options.isColorBlindModeEnabled}
      isSyntaxHighlightingEnabled={options.isSyntaxHighlightingEnabled}
    />
  </File>
);

/**
 * Sets before and after extra lines properties of the diff to `false`
 * @param diff Diff object that's chunks will be modified here
 * @returns `DiffEntry` with `hasMoreLines` set to false
 */
const hideMoreLinesInDiff = (diff: DiffType): DiffEntry => ({
  chunks: diff.chunks.map(chunk => ({
    ...chunk,
    extra: {
      before: {
        hasMoreLines: false,
      },
      after: {
        hasMoreLines: false,
      },
    },
  })),
});

/**
 * Parses the diff from context lines of a comment
 * @param comment The comment for which to get the context
 * @returns The parsed diff or null if the comment is not inline
 */
const getDiffsForComment = (
  comment: ApiCommentOrPlaceholder
): DiffEntry | null => {
  if (comment.inline && comment.inline.context_lines) {
    const parsedDiffs = diffParser(comment.inline.context_lines);
    if (parsedDiffs && parsedDiffs[0]) {
      return hideMoreLinesInDiff(parsedDiffs[0]);
    }
  }
  return null;
};

export interface ConversationWithContextProps {
  commentThread: CommentThread;
  options: RenderConversationOptions;
}

export function ConversationWithContext({
  commentThread,
  options,
}: ConversationWithContextProps): JSX.Element | null {
  const tabSize = useSelector(getGlobalTabSize);
  const diff = getDiffsForComment(commentThread.topComment);
  return diff
    ? renderInDiffConversation(diff, commentThread, options, tabSize)
    : renderConversation(commentThread, options);
}
